import { useCallback, useEffect, useState, type ReactNode } from "react";

// Inline-SVG charts for advice results, in the ValueChart idiom (design.md):
// fjord solid for the primary series, amber dashed for the comparison, a soft
// fjord band for ranges. No chart library.

export interface Pt {
  date: string;
  value: number;
}

export interface Series {
  label: string;
  points: Pt[];
  kind: "main" | "compare";
}

const W = 720;
const H = 220;
const PAD = { top: 14, right: 14, bottom: 26, left: 86 };

function compact(v: number, unit: string): string {
  if (unit === "%") return `${v.toFixed(0)}`;
  if (unit === "") return v.toFixed(0);
  const abs = Math.abs(v);
  const s =
    abs >= 1e6 ? `${(v / 1e6).toFixed(1)}m` : abs >= 1e4 ? `${Math.round(v / 1e3)}k` : Math.round(v).toLocaleString("en-GB");
  return `${unit} ${s}`;
}

/**
 * Line chart over dates. `band` draws a shaded range (e.g. 5–95 % forecast
 * outcomes) behind the lines. `unit` prefixes axis labels ("NOK", "" for an
 * index).
 */
export function SeriesChart({
  series,
  band,
  unit,
  ariaLabel,
}: {
  series: Series[];
  band?: { label: string; lower: Pt[]; upper: Pt[] } | null;
  unit: string;
  ariaLabel: string;
}) {
  const main = series.find((s) => s.points.length >= 2);
  if (!main) return <div className="muted">Not enough data to chart yet.</div>;

  const all = [...series.flatMap((s) => s.points), ...(band ? [...band.lower, ...band.upper] : [])];
  let min = Math.min(...all.map((p) => p.value));
  let max = Math.max(...all.map((p) => p.value));
  if (max === min) {
    max += 1;
    min -= 1;
  }
  const pad = (max - min) * 0.06;
  min -= pad;
  max += pad;
  const times = all.map((p) => Date.parse(p.date)).filter(Number.isFinite);
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const x = (d: string) => PAD.left + ((Date.parse(d) - t0) / (t1 - t0 || 1)) * (W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + (1 - (v - min) / (max - min)) * (H - PAD.top - PAD.bottom);
  const path = (pts: Pt[]) =>
    pts.map((p, i) => `${i === 0 ? "M" : "L"}${x(p.date).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const bandPath =
    band && band.lower.length >= 2 && band.upper.length >= 2
      ? `${path(band.upper)} ${[...band.lower]
          .reverse()
          .map((p) => `L${x(p.date).toFixed(1)},${y(p.value).toFixed(1)}`)
          .join(" ")} Z`
      : null;
  const grid = [min + (max - min) * 0.1, (min + max) / 2, min + (max - min) * 0.9];
  const first = new Date(t0).toISOString().slice(0, 10);
  const last = new Date(t1).toISOString().slice(0, 10);

  return (
    <figure className="value-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
        {grid.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} className="chart-grid" />
            <text x={PAD.left - 6} y={y(v) + 3} className="chart-label" textAnchor="end">
              {compact(v, unit)}
            </text>
          </g>
        ))}
        {bandPath && <path d={bandPath} className="adv-chart-band" />}
        {series
          .filter((s) => s.kind === "compare" && s.points.length >= 2)
          .map((s) => (
            <path key={s.label} d={path(s.points)} className="chart-line-benchmark" />
          ))}
        {series
          .filter((s) => s.kind === "main" && s.points.length >= 2)
          .map((s) => (
            <path key={s.label} d={path(s.points)} className="chart-line-main" />
          ))}
        <text x={PAD.left} y={H - 7} className="chart-label">
          {first}
        </text>
        <text x={W - PAD.right} y={H - 7} className="chart-label" textAnchor="end">
          {last}
        </text>
      </svg>
      <figcaption className="chart-legend">
        {series.map((s) => (
          <span key={s.label} className="adv-legend-item">
            <span className={s.kind === "main" ? "legend-swatch" : "legend-swatch swatch-benchmark adv-swatch-flush"} />
            {s.label}
          </span>
        ))}
        {band && (
          <span className="adv-legend-item">
            <span className="adv-swatch-band" />
            {band.label}
          </span>
        )}
      </figcaption>
    </figure>
  );
}

// Categorical fills from the Andersen palette only, in a calm order.
export const CATEGORY_FILLS = [
  "var(--c-fjord)",
  "var(--c-amber)",
  "var(--c-moss)",
  "var(--c-fjord-soft)",
  "var(--c-amber-deep)",
  "var(--c-ink-muted)",
  "var(--c-border)",
];

export interface Slice {
  label: string;
  pct: number;
}

/** One stacked 100 % bar plus a legend table — allocation at a glance. */
export function AllocationBar({ slices, title }: { slices: Slice[]; title?: string }) {
  const rows = slices.filter((s) => s.pct > 0).sort((a, b) => b.pct - a.pct);
  if (rows.length === 0) return <div className="muted">No allocation data.</div>;
  const total = rows.reduce((a, s) => a + s.pct, 0) || 1;
  let acc = 0;
  return (
    <div className="adv-alloc">
      <svg viewBox="0 0 720 18" className="adv-alloc-bar" role="img" aria-label={title ?? "Allocation"} preserveAspectRatio="none">
        {rows.map((s, i) => {
          const w = (s.pct / total) * 720;
          const r = <rect key={s.label} x={acc} y={0} width={Math.max(w - 1, 0)} height={18} fill={CATEGORY_FILLS[i % CATEGORY_FILLS.length]} />;
          acc += w;
          return r;
        })}
      </svg>
      <ul className="adv-alloc-legend">
        {rows.map((s, i) => (
          <li key={s.label}>
            <span className="adv-dot" style={{ background: CATEGORY_FILLS[i % CATEGORY_FILLS.length] }} />
            <span className="adv-alloc-label">{s.label}</span>
            <span className="adv-alloc-pct">{s.pct.toFixed(1)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Horizontal bars for a ranked breakdown (regions, sectors). */
export function BarList({ rows, max = 10 }: { rows: Slice[]; max?: number }) {
  const top = rows.filter((r) => r.pct > 0).sort((a, b) => b.pct - a.pct).slice(0, max);
  if (top.length === 0) return <div className="muted">No data.</div>;
  const peak = Math.max(...top.map((r) => r.pct)) || 1;
  return (
    <ul className="adv-barlist">
      {top.map((r) => (
        <li key={r.label}>
          <span className="adv-barlist-label">{r.label}</span>
          <span className="adv-barlist-track">
            <span className="adv-barlist-fill" style={{ width: `${(r.pct / peak) * 100}%` }} />
          </span>
          <span className="adv-barlist-pct">{r.pct.toFixed(1)} %</span>
        </li>
      ))}
    </ul>
  );
}

// ---- Portfolio composition: donut + legend, with a Chart | Table switch ----------------

/** One holding of a portfolio, as the donut and its legend need it. */
export interface Holding {
  key: string;
  name: string;
  /** Percent of the portfolio (0–100). */
  weight: number;
  amount?: number | null;
  assetClass?: string | null;
  isin?: string | null;
}

// The allocation bar's calm order first, then lighter tints of the same five
// hues — so a many-fund portfolio still reads as one Andersen palette.
const tint = (v: string, pct: number) => `color-mix(in srgb, ${v} ${pct}%, var(--c-paper))`;
export const PIE_FILLS = [
  "var(--c-fjord)",
  "var(--c-amber)",
  "var(--c-moss)",
  "var(--c-fjord-soft)",
  "var(--c-amber-deep)",
  "var(--c-ink-muted)",
  tint("var(--c-fjord)", 55),
  tint("var(--c-amber)", 55),
  tint("var(--c-moss)", 55),
  tint("var(--c-fjord-soft)", 50),
  tint("var(--c-amber-deep)", 50),
  tint("var(--c-ink-muted)", 45),
  tint("var(--c-fjord)", 30),
  tint("var(--c-amber)", 30),
  tint("var(--c-moss)", 30),
];

interface PieSlice {
  key: string;
  label: string;
  sub: string | null;
  weight: number;
  amount: number | null;
}

const PIE = { size: 220, r: 100, inner: 62 };

// An annulus sector from angle a0 to a1 (radians, 0 = 12 o'clock, clockwise).
function sectorPath(a0: number, a1: number): string {
  const c = PIE.size / 2;
  const pt = (r: number, a: number) => `${(c + r * Math.sin(a)).toFixed(2)},${(c - r * Math.cos(a)).toFixed(2)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return [
    `M${pt(PIE.r, a0)}`,
    `A${PIE.r},${PIE.r} 0 ${large} 1 ${pt(PIE.r, a1)}`,
    `L${pt(PIE.inner, a1)}`,
    `A${PIE.inner},${PIE.inner} 0 ${large} 0 ${pt(PIE.inner, a0)}`,
    "Z",
  ].join(" ");
}

function groupByAssetClass(holdings: Holding[]): PieSlice[] {
  const by = new Map<string, PieSlice & { n: number }>();
  for (const h of holdings) {
    const label = h.assetClass?.trim() || "Other";
    const g = by.get(label) ?? { key: `ac:${label}`, label, sub: null, weight: 0, amount: null, n: 0 };
    g.weight += h.weight;
    if (typeof h.amount === "number") g.amount = (g.amount ?? 0) + h.amount;
    g.n += 1;
    by.set(label, g);
  }
  return [...by.values()].map(({ n, ...g }) => ({ ...g, sub: `${n} fund${n === 1 ? "" : "s"}` }));
}

function fmtAmount(v: number | null, currency: string): string {
  if (v === null || !Number.isFinite(v)) return "—";
  return `${currency ? `${currency} ` : ""}${Math.round(v).toLocaleString("nb-NO")}`;
}

/**
 * Donut of a portfolio by holding (or by asset class, when the data has
 * them), with a legend of name, weight and amount. Hovering or focusing a
 * slice or a legend row highlights both. Inline SVG, no library.
 */
export function DonutChart({ holdings, currency, ariaLabel }: { holdings: Holding[]; currency: string; ariaLabel: string }) {
  const hasClasses = holdings.some((h) => !!h.assetClass);
  const [by, setBy] = useState<"holding" | "class">("holding");
  const [active, setActive] = useState<string | null>(null);
  const mode = hasClasses ? by : "holding";

  const slices: PieSlice[] = (
    mode === "class"
      ? groupByAssetClass(holdings)
      : holdings.map((h) => ({ key: h.key, label: h.name, sub: h.assetClass ?? h.isin ?? null, weight: h.weight, amount: h.amount ?? null }))
  )
    .filter((s) => s.weight > 0)
    .sort((a, b) => b.weight - a.weight);
  if (slices.length === 0) return <div className="muted">No holdings to chart.</div>;

  const total = slices.reduce((a, s) => a + s.weight, 0) || 1;
  const totalAmount = slices.every((s) => s.amount === null) ? null : slices.reduce((a, s) => a + (s.amount ?? 0), 0);
  const fill = (i: number) => PIE_FILLS[i % PIE_FILLS.length]!;
  const activeSlice = slices.find((s) => s.key === active) ?? null;

  let acc = 0;
  const arcs = slices.map((s, i) => {
    const a0 = (acc / total) * 2 * Math.PI;
    acc += s.weight;
    const a1 = (acc / total) * 2 * Math.PI;
    // A single 100 % slice can't be one arc (start = end); split it in two.
    const d = a1 - a0 >= 2 * Math.PI - 1e-6 ? `${sectorPath(0, Math.PI)} ${sectorPath(Math.PI, 2 * Math.PI)}` : sectorPath(a0, a1);
    return { s, i, d };
  });
  const c = PIE.size / 2;

  return (
    <div className="adv-donut">
      {hasClasses && (
        <div className="seg adv-seg adv-seg-small" role="radiogroup" aria-label="Group the chart by">
          {(
            [
              ["holding", "Holdings"],
              ["class", "Asset class"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={mode === v}
              className={mode === v ? "seg-option active" : "seg-option"}
              onClick={() => {
                setBy(v);
                setActive(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="adv-donut-body" onMouseLeave={() => setActive(null)}>
        <svg viewBox={`0 0 ${PIE.size} ${PIE.size}`} className="adv-donut-svg" role="img" aria-label={ariaLabel}>
          {arcs.map(({ s, i, d }) => (
            <path
              key={s.key}
              d={d}
              style={{ fill: fill(i) }}
              className={active && active !== s.key ? "adv-donut-slice dim" : "adv-donut-slice"}
              tabIndex={0}
              aria-label={`${s.label}: ${((s.weight / total) * 100).toFixed(1)} %`}
              onMouseEnter={() => setActive(s.key)}
              onFocus={() => setActive(s.key)}
              onBlur={() => setActive(null)}
            />
          ))}
          <text x={c} y={c - 4} textAnchor="middle" className="adv-donut-centre-value">
            {activeSlice ? `${((activeSlice.weight / total) * 100).toFixed(1)} %` : fmtAmount(totalAmount, "") === "—" ? "100 %" : fmtAmount(totalAmount, "")}
          </text>
          <text x={c} y={c + 16} textAnchor="middle" className="adv-donut-centre-label">
            {activeSlice
              ? activeSlice.label.length > 22
                ? `${activeSlice.label.slice(0, 21)}…`
                : activeSlice.label
              : totalAmount !== null
                ? `${currency} · ${slices.length} ${mode === "class" ? "asset classes" : "holdings"}`
                : `${slices.length} ${mode === "class" ? "asset classes" : "holdings"}`}
          </text>
        </svg>
        <ul className="adv-donut-legend">
          {slices.map((s, i) => (
            <li
              key={s.key}
              tabIndex={0}
              className={active === s.key ? "active" : active ? "dim" : undefined}
              onMouseEnter={() => setActive(s.key)}
              onFocus={() => setActive(s.key)}
              onBlur={() => setActive(null)}
            >
              <span className="adv-dot" style={{ background: fill(i) }} />
              <span className="adv-donut-name">
                {s.label}
                {s.sub && <span className="muted">{s.sub}</span>}
              </span>
              <span className="adv-donut-pct">{((s.weight / total) * 100).toFixed(1)} %</span>
              <span className="adv-donut-amt">{fmtAmount(s.amount, "")}</span>
            </li>
          ))}
          <li className="adv-donut-total" aria-hidden="true">
            <span />
            <span className="adv-donut-name">Total</span>
            <span className="adv-donut-pct">100 %</span>
            <span className="adv-donut-amt">{fmtAmount(totalAmount, currency)}</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// The Chart | Table choice is one preference for the whole workbench: kept in
// localStorage and shared live between every composition block on the page.
const VIEW_KEY = "adv.compositionView";
const VIEW_EVENT = "adv:composition-view";
type CompositionMode = "chart" | "table";

function readView(): CompositionMode {
  try {
    return window.localStorage.getItem(VIEW_KEY) === "table" ? "table" : "chart";
  } catch {
    return "chart";
  }
}

function useCompositionView(): [CompositionMode, (m: CompositionMode) => void] {
  const [mode, setMode] = useState<CompositionMode>(readView);
  useEffect(() => {
    const sync = () => setMode(readView());
    window.addEventListener(VIEW_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(VIEW_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const set = useCallback((m: CompositionMode) => {
    try {
      window.localStorage.setItem(VIEW_KEY, m);
    } catch {
      /* private mode — the choice just isn't remembered */
    }
    setMode(m);
    window.dispatchEvent(new Event(VIEW_EVENT));
  }, []);
  return [mode, set];
}

/**
 * A portfolio's composition block: an optional label on the left, the
 * `.seg` Chart | Table switch top-right, then the donut or the caller's table
 * (the existing holdings table with its tfoot total). Chart is the default.
 */
export function CompositionView({
  holdings,
  currency,
  title,
  table,
  chartNote,
}: {
  holdings: Holding[];
  currency: string;
  title?: ReactNode;
  table: ReactNode;
  /** Shown under the chart, e.g. "Switch to Table to change amounts". */
  chartNote?: ReactNode;
}) {
  const [mode, setMode] = useCompositionView();
  return (
    <div className="adv-composition">
      <div className="adv-composition-head">
        {title ? <span className="adv-label">{title}</span> : <span />}
        <div className="seg adv-seg adv-seg-small" role="radiogroup" aria-label="Show the composition as">
          {(
            [
              ["chart", "Chart"],
              ["table", "Table"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={mode === v}
              className={mode === v ? "seg-option active" : "seg-option"}
              onClick={() => setMode(v)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {mode === "chart" ? (
        <>
          <DonutChart holdings={holdings} currency={currency} ariaLabel={typeof title === "string" ? title : "Portfolio composition"} />
          {chartNote}
        </>
      ) : (
        table
      )}
    </div>
  );
}
